import { Logger } from './logger';

export interface DomainConfig {
  subdomain: string;
  name: string;
  requiresAuth: boolean;
  publicRoutes: string[];
  defaultRoute: string;
}

// Domain configuration for different subdomains
export const DOMAIN_CONFIG: Record<string, DomainConfig> = {
  catalog: {
    subdomain: 'catalog',
    name: 'Product Catalog',
    requiresAuth: false,
    publicRoutes: ['/login', '/auth', '/api/auth', '/api/public', '/', '/catalog', '/home'],
    defaultRoute: '/catalog',
  },
  erp: {
    subdomain: 'erp',
    name: 'ERP System',
    requiresAuth: true,
    publicRoutes: ['/login', '/auth', '/api/auth', '/api/public'],
    defaultRoute: '/dashboard',
  },
};

export function extractSubdomain(hostname: string): string | null {
  if (!hostname) return null;

  // Handle localhost development with different patterns:
  // - erp.local:4000 -> 'erp'
  // - catalog.local:4000 -> 'catalog'
  // - localhost:5000 -> null (fallback)
  if (hostname.includes('.local')) {
    const parts = hostname.split('.');
    return parts[0];
  }

  // Handle production domains:
  // Both sconlinesolution.com and easybizgrow.com
  // - erp.sconlinesolution.com -> 'erp'
  // - catalog.sconlinesolution.com -> 'catalog'
  // - erp.easybizgrow.com -> 'erp'
  // - catalog.easybizgrow.com -> 'catalog'
  const parts = hostname.split('.');
  
  // For domains like catalog.sconlinesolution.com, catalog.easybizgrow.com, etc.
  if (parts.length >= 3) {
    const subdomain = parts[0];
    const rootDomain = parts.slice(-2).join('.'); // Get the last two parts (e.g., 'easybizgrow.com')
    
    // Support both domain families
    if (rootDomain === 'sconlinesolution.com' || rootDomain === 'easybizgrow.com') {
      return subdomain === 'www' ? null : subdomain;
    }
    
    // Fallback for other domain patterns
    return subdomain === 'www' ? null : subdomain;
  }

  return null;
}

export function getDomainFamily(hostname: string): string | null {
  if (!hostname) return null;
  
  const parts = hostname.split('.');
  
  // Handle localhost
  if (hostname.includes('.local') || hostname.includes('localhost')) {
    return 'local';
  }
  
  // Get the root domain (last two parts)
  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join('.');
    
    if (rootDomain === 'sconlinesolution.com') return 'sconlinesolution.com';
    if (rootDomain === 'easybizgrow.com') return 'easybizgrow.com';
  }
  
  return null;
}

export function getDomainConfig(hostname: string): DomainConfig | null {
  const subdomain = extractSubdomain(hostname);
  return subdomain ? DOMAIN_CONFIG[subdomain] || null : null;
}

export function constructBaseUrl(hostname: string): string {
  const nodeEnv = (process.env.NODE_ENV || '').toLowerCase();
  const isProduction = nodeEnv === 'production' || nodeEnv === 'staging';
  const protocol = isProduction ? 'https' : 'http';
  
  // For development, use the exact hostname as provided by Caddy
  if (!isProduction) {
    return `${protocol}://${hostname}`;
  }
  
  // For production/staging, construct proper URL
  return `${protocol}://${hostname}`;
}

export function isPublicRoute(pathname: string, domainConfig?: DomainConfig | null): boolean {
  // Always public routes (regardless of domain)
  const globalPublicRoutes = [
    '/_next/',
    '/favicon.ico',
    '/error/',
    '/robots.txt',
    '/sitemap.xml',
    '/manifest.json',
    '/login',      // Login should always be public
    '/auth/',      // Auth routes should always be public
    '/api/auth/',  // NextAuth API routes should always be public
    '/home',       // Home page should always be public
  ];

  // Check global public routes
  for (const route of globalPublicRoutes) {
    if (pathname === route || pathname.startsWith(route)) {
      return true;
    }
  }

  // Check domain-specific public routes
  if (domainConfig?.publicRoutes) {
    for (const route of domainConfig.publicRoutes) {
      if (pathname === route || pathname.startsWith(route + '/')) {
        return true;
      }
    }
  }

  return false;
}

export function logDomainInfo(hostname: string, pathname: string): void {
  const subdomain = extractSubdomain(hostname);
  const domainConfig = getDomainConfig(hostname);
  const baseUrl = constructBaseUrl(hostname);
  const nodeEnv = (process.env.NODE_ENV || '').toLowerCase();
  const isProduction = nodeEnv === 'production' || nodeEnv === 'staging';
  
  // Always log in production/staging for debugging
  if (isProduction || nodeEnv === 'development') {
    Logger.log('Domain', 'Domain analysis:', {
      hostname,
      pathname,
      subdomain,
      domainName: domainConfig?.name,
      requiresAuth: domainConfig?.requiresAuth,
      baseUrl,
      isPublic: isPublicRoute(pathname, domainConfig),
      nodeEnv: process.env.NODE_ENV,
      hostnameLength: hostname.length,
      hostnameParts: hostname.split('.'),
    });
  }
}
